/** Motion Asset node — skeleton preview, no full sequence on mount. */

import { Handle, Position, useUpdateNodeInternals, type NodeProps } from '@xyflow/react'
import { memo, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCanvasNodeRegistryI18n } from '../nodeRegistryI18n'
import { useNodeDefinition } from '../nodeRegistry'
import MotionSkeletonPreview from '../components/MotionSkeletonPreview'
import { loadMotionPreviewDeduped, type MotionPreviewPayload } from '../motionAssetPreview'
import type { FlowNodeData } from '../types'

const NODE_WIDTH = 300
const PREVIEW_HEIGHT = 160

function MotionAssetNodeImpl({ id, data, selected }: NodeProps) {
  const { t } = useTranslation('canvas')
  const { nodeInstanceLabel } = useCanvasNodeRegistryI18n()
  const nodeData = data as unknown as FlowNodeData & {
    workspaceId?: string
  }
  const def = useNodeDefinition(nodeData.nodeType)
  const label = nodeInstanceLabel(nodeData.nodeType, nodeData.fields?.label as string | undefined, def.label)
  const motionAssetId = (nodeData.entityId as string | undefined) || (nodeData.fields?.motionAssetId as string | undefined)
  const motionType = (nodeData.fields?.motionType as string | undefined) || 'MOTION_2D'
  const workspaceId = nodeData.workspaceId || ''
  const [preview, setPreview] = useState<MotionPreviewPayload | null>(null)
  const updateNodeInternals = useUpdateNodeInternals()

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => updateNodeInternals(id))
    return () => window.cancelAnimationFrame(frame)
  }, [id, updateNodeInternals])

  useEffect(() => {
    setPreview(null)
    if (!workspaceId || !motionAssetId) return
    let cancelled = false
    loadMotionPreviewDeduped(workspaceId, motionAssetId)
      .then((payload) => {
        if (!cancelled) setPreview(payload)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [motionAssetId, workspaceId])

  return (
    <div
      className={`pcv-node pcv-node--media pcv-node--motion-asset${selected ? ' is-selected' : ''}`}
      style={{ width: NODE_WIDTH }}
      data-testid="canvas-motion-asset-node"
    >
      <div className="pcv-node-inner">
        <div className="pcv-media-header">
          <span className="pcv-media-icon" style={{ color: def.color, background: `${def.color}22` }}>
            {def.icon}
          </span>
          <span className="pcv-media-title" title={label}>{label}</span>
          <span className="pcv-motion-type-badge">{motionType === 'MOTION_3D' ? '3D' : '2D'}</span>
        </div>
        <div className="pcv-media-body" style={{ height: PREVIEW_HEIGHT }}>
          {preview?.frames?.length ? (
            <MotionSkeletonPreview
              frames={preview.frames}
              width={NODE_WIDTH}
              height={PREVIEW_HEIGHT}
              fps={preview.preview_fps || 12}
              coordinateSpace={preview.coordinate_space}
              loop
            />
          ) : (
            <div className="pcv-media-placeholder">
              <span className="pcv-media-placeholder-hint">{t('motionAsset.preview.loading')}</span>
            </div>
          )}
        </div>
      </div>
      <Handle type="source" position={Position.Right} id="motion-out" className="pcv-handle pcv-handle--lg" />
      <Handle type="target" position={Position.Left} id="motion-in" className="pcv-handle pcv-handle--lg pcv-handle-inert" />
    </div>
  )
}

export default memo(MotionAssetNodeImpl)
